import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, Calendar, ArrowRight, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";
import { getBlogPost } from "@/lib/blog";

const SITE_URL = "https://myflovea.com";

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] leading-tight mt-14 mb-6">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] leading-snug mt-8 mb-3">
          {block.text}
        </h3>
      );
    case "paragraph":
      return (
        <p className="text-[17px] leading-[1.75] text-[#3D2B1F] mt-4">{block.text}</p>
      );
    case "list":
      return (
        <ul className="mt-4 space-y-3 list-disc pl-6 text-[17px] leading-[1.75] text-[#3D2B1F] marker:text-[#8B6343]">
          {block.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      );
    case "cta":
      return (
        <div
          data-testid="blog-cta"
          className="mt-14 bg-[#F5EFE6] border border-[#dcd7ce] rounded-md px-8 py-10 text-center"
        >
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A]">
            {block.title}
          </h3>
          <p className="mt-4 text-[15px] leading-relaxed text-[#4a4a4a] max-w-xl mx-auto">
            {block.text}
          </p>
          <Link
            to={block.href}
            data-testid="blog-cta-link"
            className="mt-6 inline-flex items-center gap-2 bg-[#3D2B1F] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase rounded-md hover:bg-[#1A1A1A] transition-colors"
          >
            {block.buttonLabel}
            <ArrowRight size={14} />
          </Link>
        </div>
      );
    default:
      return null;
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const post = getBlogPost(slug);

  if (!post) return <Navigate to="/" replace />;

  const canonical = `${SITE_URL}/blog/${post.slug}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    image: [`${SITE_URL}${post.hero}`],
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: { "@type": "Organization", name: "myFLOVEA" },
    publisher: {
      "@type": "Organization",
      name: "myFLOVEA",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/photos/hero-myflovea.webp`,
      },
    },
    mainEntityOfPage: canonical,
    articleSection: post.category,
    inLanguage: post.lang,
  };

  const isFr = lang === "fr";
  const formattedDate = new Date(post.publishedAt).toLocaleDateString(
    post.lang === "fr" ? "fr-FR" : "en-US",
    { year: "numeric", month: "long", day: "numeric" }
  );

  return (
    <article data-testid="blog-post" className="bg-[#FAF6F0]">
      <Seo
        lang={post.lang}
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.metaDescription}
        image={`${SITE_URL}${post.hero}`}
      />
      <Helmet>
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:modified_time" content={post.updatedAt || post.publishedAt} />
        <meta property="article:section" content={post.category} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero image full-width */}
      <div className="w-full h-[42vh] md:h-[60vh] relative overflow-hidden bg-[#ebe7df]">
        <img
          src={post.hero}
          alt={post.heroAlt}
          className="w-full h-full object-cover"
          loading="eager"
        />
      </div>

      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16 md:py-20">
        <Link
          to="/"
          data-testid="blog-back-home"
          className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#8B6343] hover:text-[#3D2B1F] transition-colors"
        >
          <ArrowLeft size={14} />
          {isFr ? "Retour à la boutique" : "Back to shop"}
        </Link>

        <div className="mt-8 flex flex-wrap items-center gap-4 text-xs tracking-[0.18em] uppercase text-[#8B6343]">
          <span data-testid="blog-category">{post.category}</span>
          <span className="opacity-40">·</span>
          <span className="inline-flex items-center gap-2">
            <Calendar size={12} strokeWidth={1.6} />
            {formattedDate}
          </span>
          <span className="opacity-40">·</span>
          <span className="inline-flex items-center gap-2">
            <Clock size={12} strokeWidth={1.6} />
            {post.readingMinutes} min
          </span>
        </div>

        <h1
          data-testid="blog-title"
          className="mt-4 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]"
        >
          {post.title}
        </h1>

        <p className="mt-6 text-[19px] leading-relaxed text-[#4a4a4a] font-light">
          {post.excerpt}
        </p>

        <div className="mt-10 border-t border-[#dcd7ce]" />

        <div data-testid="blog-content">
          {post.blocks.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </div>

        {/* Footer links */}
        <div className="mt-20 pt-10 border-t border-[#dcd7ce] flex flex-col sm:flex-row items-center justify-between gap-6 text-sm text-[#4a4a4a]">
          <div>
            {isFr ? "Publié par " : "Published by "}
            <span className="text-[#1A1A1A]">{post.author}</span>
          </div>
          <Link
            to="/product/coussin-massant-chauffant"
            data-testid="blog-footer-product-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#3D2B1F] underline underline-offset-[6px] decoration-[#3D2B1F]/30 hover:decoration-[#3D2B1F] transition-colors"
          >
            {isFr ? "Voir le Coussin Massant Chauffant" : "See the Heated Massage Cushion"}
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogPost;
